import { useCallback } from 'react';
import { useFetcher } from 'react-router';

import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { ActionResult } from '@jane/bloom/types/remixAction';
import { deriveBasename } from '@jane/bloom/utils/deriveBasename';
import { buildFormData } from '@jane/bloom/utils/formData';

interface ResetPasswordRequestOptions {
  /**
   * When true, the action skips its default redirect to `/login` and returns
   * `{ success }` JSON. Callers driving their own UI (e.g. the in-page auth
   * modal) should pass this.
   */
  skipRedirect?: boolean;
}

export function useResetPasswordRequestFetcher({
  skipRedirect,
}: ResetPasswordRequestOptions = {}) {
  const { data, state, submit } = useFetcher<ActionResult>();
  const { logo, menuUrl, operator, theme } = useRootLoaderData();

  const sendResetPasswordRequest = useCallback(
    (email: string) => {
      const formBody = {
        email,
        from_name: operator?.name,
        link: `https://${window.location.host}${deriveBasename(menuUrl)}/reset-password`,
        logo_url: logo?.small,
        skipRedirect: skipRedirect ? 'true' : 'false',
        text_color: theme?.bloom?.colors?.primary?.contrast,
        theme_color: theme?.bloom?.colors?.primary?.main,
      };

      const formData = buildFormData(formBody);

      submit(formData, {
        action: `/api/reset-password-request`,
        method: 'POST',
      });
    },
    [
      logo?.small,
      menuUrl,
      operator?.name,
      skipRedirect,
      submit,
      theme?.bloom?.colors?.primary?.contrast,
      theme?.bloom?.colors?.primary?.main,
    ]
  );

  return {
    data,
    sendResetPasswordRequest,
    state,
  };
}
